import { ArrayToLookupByPredicate } from "global/Utility/TSFunctions";
import {
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IGroupConfigurationData as FAM_IGroupConfigurationData
} from "model/FlexibleAttributeModel";
import { IBenefit, IGroupConfigurationData, IPlan } from "model/FlexibleAttributeModel";
import { BaseJsonTransformer } from "./IJsonTransformer";
import { $cat_$ancillary_id, $cat_$medical_id } from "model/ConfigCategoryConstants";
import { $att_$ancillary_$bcbs_novologix_id, $att_$ancillary_$bcbs_rally_connect_id, $att_$ancillary_$bcbs_smartshopper_id, $att_$medical_$bcbs_outpatrehfac_id, $att_$medical_$bcbs_outpatrehprofac_id, $att_$medical_$bcbs_outpatrehspeechthfac_id, $att_$medical_$bcbs_pt_otfacility_id, $att_$medical_$bcbs_rxpreauth_smbm_id, $att_$medical_$bcbs_speechtherapyfacility_id } from "model/ConfigAttributeConstants";
import { $oset_$rally_connect_transparency_$custom_other_id, $oset_$rally_connect_transparency_$enhanced_directory_id, $oset_$rally_connect_transparency_$shopping_for_care_rewards_id, $oset_$rally_connect_transparency_$standard_directory_id } from "model/ConfigOptionSetConstants";

/**
 * Updates attribute sort order and adds in any missing attributes given by the release.
 * NOTE: If fields no longer exist (which shouldn't happen), the fields will be appended
 * to the end of the field array (ensuring no data loss).
 */
export class Sprint17Transformer extends BaseJsonTransformer {

    config: FAM_IGroupConfigurationConfig;
    json: FAM_IGroupConfigurationData;
    benefits: IBenefit[] = [];
    
    constructor({ config, json }: { config: FAM_IGroupConfigurationConfig, json: FAM_IGroupConfigurationData }) {
        super();
        this.config = config;
        this.json = json;
    }


    async before(json: IGroupConfigurationData): Promise<void> {
        console.debug("[tran] Sprint17Transformer.before")

        this.benefits = json.benefits;
    }

    async transformPlan(plan: IPlan): Promise<boolean | void> {

        const medicalBenefit = this.benefits
            .filter(b => b.bcbs_config_categoryid === $cat_$medical_id
                && plan.benefitRefs.some(bRef => bRef.id === b.id)
            );

        const ancillaryBenefit = this.benefits
            .filter(b => b.bcbs_config_categoryid === $cat_$ancillary_id
                && plan.benefitRefs.some(bRef => bRef.id === b.id)
            );

        if (medicalBenefit?.[0] && ancillaryBenefit?.[0]) {

            const ancillaryBenefitFieldMap = ArrayToLookupByPredicate(ancillaryBenefit[0].fields, (t) => {
                return t.configRef.id
            });

            const medicalBenefitFieldMap = ArrayToLookupByPredicate(medicalBenefit[0].fields, (t) => {
                return t.configRef.id
            });

            // Rx PreAuth - Specialty Medical Benefit Management | Ancillary
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c478ae9c-2759-ec11-8f8f-000d3a597a8a
            const bcbs_novologix = ancillaryBenefitFieldMap[$att_$ancillary_$bcbs_novologix_id];

            // Rx Pre-Auth - SMBM | Medical
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&pagetype=entityrecord&etn=bcbs_config_attribute&id=5d1b4bb4-720f-ee11-8f6d-000d3a305861
            const bcbs_RxPreAuth_SMBM = medicalBenefitFieldMap[$att_$medical_$bcbs_rxpreauth_smbm_id];

            bcbs_novologix.values.forEach(sourceValue => {
                bcbs_RxPreAuth_SMBM.values.forEach(targetValue => {
                    if (sourceValue.value)
                        targetValue.value = structuredClone(sourceValue.value);
                });
            });

        }
    }

    async transformBenefit(benefit: IBenefit): Promise<boolean | void> {
        // state.data.config.cache.map_bcbs_config_category_named.Medical

        // Medical ?
        if (benefit.bcbs_config_categoryid === $cat_$medical_id) {

            const fieldMap = ArrayToLookupByPredicate(benefit.fields, (t) => {
                return t.configRef.id
            });

            // https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%2017?workitem=341845
            // https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%2017?workitem=399100

            // Pay outpatient facility services for speech therapy / Costshare
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&pagetype=entityrecord&etn=bcbs_config_attribute&id=1ead0416-076f-ec11-8941-00224804a95e
            const bcbs_outpatrehspeechthfac = fieldMap[$att_$medical_$bcbs_outpatrehspeechthfac_id];

            // Speech Therapy Facility
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&pagetype=entityrecord&etn=bcbs_config_attribute&id=abeb2cec-6a10-ee11-8f6d-000d3a305861
            const bcbs_Speechtherapyfacility = fieldMap[$att_$medical_$bcbs_speechtherapyfacility_id];




            // Pay outpatient professional services for physical / occupational therapy performed in a facility
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&pagetype=entityrecord&etn=bcbs_config_attribute&id=0ead0416-076f-ec11-8941-00224804a95e
            const bcbs_outpatrehprofac = fieldMap[$att_$medical_$bcbs_outpatrehprofac_id];

            // PT/OT Facility
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=7bd0a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=0cad0416-076f-ec11-8941-00224804a95e
            const bcbs_outpatrehfac = fieldMap[$att_$medical_$bcbs_outpatrehfac_id]

            // PT/OT Facility
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&pagetype=entityrecord&etn=bcbs_config_attribute&id=4fc878cf-6910-ee11-8f6d-000d3a305861
            const bcbs_pt_otfacility = fieldMap[$att_$medical_$bcbs_pt_otfacility_id];



            // find the last effective date gold standard, before create on before 7/4, and compare to gold standards vs 

            // Commenting Out for now
            // TODO test this
            if (!bcbs_Speechtherapyfacility.values.some(q => q.value?.type))
                bcbs_outpatrehspeechthfac.values.forEach(sourceValue => {
                    // target has no pcp or specialist split, so only tiers we have to take care
                    bcbs_Speechtherapyfacility.values
                        .filter(targetValue => targetValue.tierRef?.id === sourceValue.tierRef?.id)
                        .forEach(targetValue => {
                            if (sourceValue.value)
                                if (!targetValue.value || (targetValue.value && sourceValue.type === 'specialist'))
                                    targetValue.value = structuredClone(sourceValue.value);
                        })
                });

            // TODO test this
            if (!bcbs_pt_otfacility.values.some(q => q.value?.type))
                bcbs_outpatrehfac.values.forEach(sourceValue => {
                    // target has no pcp or specialist split, so only tiers we have to take care
                    bcbs_pt_otfacility.values
                        .filter(targetValue => targetValue.tierRef?.id === sourceValue.tierRef?.id)
                        .forEach(targetValue => {
                            if (sourceValue.value)
                                if (!targetValue.value || (targetValue.value && sourceValue.type === 'specialist'))
                                    targetValue.value = structuredClone(sourceValue.value);
                        })
                });
        }


        // Ancillary?
        if (benefit.bcbs_config_categoryid === $cat_$ancillary_id) {

            const fieldMap = ArrayToLookupByPredicate(benefit.fields, (t) => {
                return t.configRef.id
            });

            // https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%2018?workitem=399820

            // Shopping for Care
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=b478ae9c-2759-ec11-8f8f-000d3a597a8a
            const bcbs_rally_connect = fieldMap[$att_$ancillary_$bcbs_rally_connect_id];

            // SmartShopper
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=5ad1db9e-8d0f-ee11-8f6d-00224808e3f5
            const bcbs_smartshopper = fieldMap[$att_$ancillary_$bcbs_smartshopper_id];

            // https://dev.azure.com/blue-infosys/Group%20Library/_sprints/taskboard/Group%20Library%20Team/Group%20Library/Sprint%2018?workitem=399820
            bcbs_rally_connect.values.forEach(sourceValue => {

                // SmartShopper Update
                // --------------------------------------------
                bcbs_smartshopper.values.forEach(targetValue => {
                    if (sourceValue.value?.type === 'entity' && (sourceValue.value.id === $oset_$rally_connect_transparency_$shopping_for_care_rewards_id || sourceValue.value.name === 'Shopping for Care + Rewards')) {
                        targetValue.value = { type: 'bool', value: true }
                    } else {
                        targetValue.value = { type: 'bool', value: false }
                    }
                });

                // Shopping for Care Update
                // --------------------------------------------
                if (sourceValue.value?.type === 'entity' && (sourceValue.value.id === $oset_$rally_connect_transparency_$shopping_for_care_rewards_id || sourceValue.value.name === 'Shopping for Care + Rewards')) {
                    // Standard Directory - https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_optionsetitem&id=cbff1306-840f-ee11-8f6d-00224808e3f5
                    sourceValue.value = {
                        type: 'entity',
                        name: 'Standard Directory',
                        entityType: 'bcbs_config_optionsetitem',
                        id: $oset_$rally_connect_transparency_$standard_directory_id
                    }
                }
                // Custom, other - https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_optionsetitem&id=95adc8fa-e358-ec11-8f8f-000d3a597a8a
                else if (sourceValue.value?.type === 'entity' && (sourceValue.value.id === $oset_$rally_connect_transparency_$custom_other_id || sourceValue.value.name === 'Custom, other')) {
                    // Enhanced Directory - https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=42d1a630-3c14-ed11-b83b-000d3a372023&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_optionsetitem&id=3d27c518-840f-ee11-8f6d-00224808e3f5
                    sourceValue.value = {
                        type: 'entity',
                        name: 'Enhanced Directory',
                        entityType: 'bcbs_config_optionsetitem',
                        id: $oset_$rally_connect_transparency_$enhanced_directory_id
                    }
                }
            })
        }
    }

    async after(json: FAM_IGroupConfigurationData): Promise<void> {
        console.debug("[tran] Sprint17Transformer.after")

        if (json.transformers)
            json.transformers.Sprint17 = true
    }

}
